// Authentication state management

const TOKEN_KEY = 'lera_token';
const API_BASE = '/api';

export interface User {
  id: string;
  email: string;
  display_name: string;
  status: 'pending' | 'approved' | 'suspended';
  is_admin: boolean;
}

export interface AuthState {
  token: string | null;
  user: User | null;
  loading: boolean;
  error: string | null;
}

// Get stored token
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

// Store token
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

// Clear token
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Check if token is expired (simple check without full JWT parsing)
export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return false;

    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

// Sessions slide: a token older than this is traded for a fresh one, so
// only a long absence (the server's full token lifetime) forces a new login.
const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000;
let refreshing: Promise<void> | null = null;

function issuedAt(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.iat === 'number' ? payload.iat * 1000 : null;
  } catch {
    return null;
  }
}

// Best-effort: a failed refresh leaves the current token in place, and a
// token that has really gone bad surfaces through the next API call's 401.
export function refreshTokenIfStale(): Promise<void> {
  const token = getToken();
  if (!token || isTokenExpired(token)) return Promise.resolve();
  const iat = issuedAt(token);
  if (iat !== null && Date.now() - iat < REFRESH_AFTER_MS) return Promise.resolve();
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) return;
      const body = await response.json() as { token?: unknown };
      // A logout or a new sign-in during the request wins over this answer.
      if (typeof body.token === 'string' && getToken() === token) setToken(body.token);
    } catch {
      // Offline or unreachable: try again next time.
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

// Fetch current user info
export async function fetchUser(token: string): Promise<User> {
  const response = await fetch(`${API_BASE}/user/me`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user');
  }

  return response.json();
}

export interface ContainerInfo {
  status: string;
  port: number;
  workspace_mode?: boolean;
  file_port?: number;
  /**
   * Token for this user's own container WebSocket, signed with a key derived
   * per user. NOT the login token: that one is signed with the manager's
   * master secret and carries the admin claim, and sending it into a container
   * is what forced every container to hold the master key. Optional so a
   * response from an older manager still parses.
   */
  ws_token?: string;
}

// Start user's container
export async function startContainer(token: string): Promise<ContainerInfo> {
  const response = await fetch(`${API_BASE}/container/start`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to start container');
  }

  return response.json();
}

// Get container status
export async function getContainerStatus(token: string): Promise<ContainerInfo> {
  const response = await fetch(`${API_BASE}/container/status`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get container status');
  }

  return response.json();
}

// Logout
export async function logout(token: string): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  clearToken();
}

// Parse OAuth callback from URL
export function parseAuthCallback(): { token?: string; pending?: boolean; error?: string } {
  const params = new URLSearchParams(window.location.search);
  const result: { token?: string; pending?: boolean; error?: string } = {};

  if (params.has('token')) {
    result.token = params.get('token')!;
  }
  if (params.has('pending')) {
    result.pending = params.get('pending') === 'true';
  }
  if (params.has('error')) {
    result.error = params.get('error')!;
  }

  // Clear URL params after reading
  if (result.token || result.error) {
    window.history.replaceState({}, '', window.location.pathname);
  }

  return result;
}

// Get OAuth login URLs
export function getGoogleAuthUrl(): string {
  return `${API_BASE}/auth/google`;
}

export function getGitHubAuthUrl(): string {
  return `${API_BASE}/auth/github`;
}

// SSH Key types and API
export interface SSHKey {
  name: string;
  fingerprint: string;
  created_at: string;
  last_used_at?: string;
}

export async function listSSHKeys(token: string): Promise<SSHKey[]> {
  const response = await fetch(`${API_BASE}/ssh/keys`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to list SSH keys');
  }

  return response.json();
}

export async function addSSHKey(token: string, name: string, publicKey: string): Promise<SSHKey> {
  const response = await fetch(`${API_BASE}/ssh/keys`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, public_key: publicKey }),
  });

  if (!response.ok) {
    const text = (await response.text()).trim();
    throw new Error(text || 'Failed to add SSH key');
  }

  return response.json();
}

export async function deleteSSHKey(token: string, fingerprint: string): Promise<void> {
  const response = await fetch(`${API_BASE}/ssh/keys/${encodeURIComponent(fingerprint)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const text = (await response.text()).trim();
    throw new Error(text || 'Failed to delete SSH key');
  }
}

// Admin API types and functions
export interface PendingUser {
  id: string;
  email: string;
  display_name: string;
  provider: string;
  created_at: string;
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  status: string;
  is_admin: boolean;
  // First port of the user's reserved 8-port block (ListAllUsers reports
  // PortBase under this JSON key) -- not a single port.
  port: number;
  created_at: string;
  // Per-user profile cap override; null means the deployment's global
  // default applies. ListAllUsers serializes this as a *int (lera-manager
  // commit 51673bd), so it is always present -- never the absent-key case
  // task-20-report.md originally described.
  max_profiles?: number | null;
  max_concurrent?: number | null;
}

export interface BaseProfile {
  name: string;
  version: number;
}

export async function fetchPendingUsers(token: string): Promise<PendingUser[]> {
  const response = await fetch(`${API_BASE}/admin/pending`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch pending users');
  }

  return response.json();
}

export async function fetchAllUsers(token: string): Promise<AdminUser[]> {
  const response = await fetch(`${API_BASE}/admin/users`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }

  return response.json();
}

export async function approveUser(token: string, userId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/admin/approve/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to approve user');
  }
}

export async function suspendUser(token: string, userId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/admin/suspend/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to suspend user');
  }
}

export async function fetchBases(token: string): Promise<BaseProfile[]> {
  const response = await fetch(`${API_BASE}/admin/bases`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch base profiles');
  }

  return response.json();
}

// Set or clear a user's two per-user limits.
//
// They are different in kind. max_profiles is how many profiles a user may
// HAVE and has no upper bound - a profile costs a directory, not a port.
// max_concurrent is how many may RUN at once and is capped at 8, because that
// is how many ports a container publishes. null clears either back to the
// deployment default.
//
// The server is the authority on both ranges -- it rejects an out-of-range
// number with a 400 rather than clamping, so callers should surface its
// message verbatim.
export async function setUserProfileLimit(
  token: string,
  userId: string,
  maxProfiles: number | null,
  maxConcurrent?: number | null,
): Promise<void> {
  const response = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(userId)}/limits`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(
      maxConcurrent === undefined
        ? { max_profiles: maxProfiles }
        : { max_profiles: maxProfiles, max_concurrent: maxConcurrent },
    ),
  });

  if (!response.ok) {
    const text = (await response.text()).trim();
    throw new Error(text || 'Failed to update profile limit');
  }
}
